// utils/atribucionFactura.js
//
// F3 del plano `docs/decisiones/gestion-atribucion-factura-conceptos-diseno.md`.
//
// Verdad derivada del eje ATRIBUCIÓN DE INGRESO (ortogonal al eje pago/cobro de aplicaciones_saldo):
// "¿cuánto del neto de una factura SII está atribuido a conceptos de venta, y cuánto le queda por
// atribuir?". La fuente única es el puente `facturas_sii_vinculaciones` (columna `monto_aplicado`,
// migración F2). Función PURA: no lee DB, recibe la factura + sus filas del puente.
//
// Reusa el núcleo de saldo de `resolverSaldo` (10.1, `computarSaldo` + TOLERANCIA_MISMA_MONEDA): NO se
// duplica la fórmula. `monto_aplicado` es SIEMPRE una porción del neto de la MISMA factura → misma
// moneda por construcción → tolerancia fija 1 (no hay caso cross-currency acá, a diferencia del cobro).

import { computarSaldo, TOLERANCIA_MISMA_MONEDA } from "./cobro/resolverSaldo";

// Importe del documento para medir la atribución. Las facturas de EXPORTACIÓN (DTE 110) llevan el monto
// en `monto_exento` con `monto_neto = 0` → un `??` tomaría 0 (0 no es nullish) y daría 'parcial' falso.
// Se trata el neto 0 como "sin neto afecto" y se cae a exento (misma unidad, 10.246). Los callers que ya
// resuelven el neto (siiMap.montoNeto = monto_neto||monto_exento) lo pasan como `monto_neto` > 0 y este
// camino lo respeta.
const netoDeFactura = (factura) => {
  const netoAfecto = Number(factura?.monto_neto) || 0;
  if (netoAfecto > 0) return netoAfecto;
  return Number(factura?.monto_exento ?? factura?.montoNeto ?? factura?.neto ?? 0);
};

/**
 * @param {{monto_neto?:number, monto_exento?:number, montoNeto?:number, neto?:number}} factura
 * @param {Array<{venta_tipo?:string, venta_ref_id?:number, venta_ref_index?:number,
 *          monto_aplicado?:number}>} filasPuente
 * @returns {{ estado:'sin_vincular'|'parcial'|'total', neto:number, aplicado:number, saldo:number,
 *             porConcepto: Array<{venta_tipo, venta_ref_id, venta_ref_index, monto_aplicado}> }}
 */
export const resolverAtribucionFactura = (factura, filasPuente = []) => {
  const neto = netoDeFactura(factura);
  const filas = Array.isArray(filasPuente) ? filasPuente : [];

  if (filas.length === 0) {
    return { estado: "sin_vincular", neto, aplicado: 0, saldo: neto, porConcepto: [] };
  }

  const aplicado = filas.reduce((acc, f) => acc + Number(f?.monto_aplicado || 0), 0);
  const { saldo, saldada } = computarSaldo(neto, aplicado, TOLERANCIA_MISMA_MONEDA);

  return {
    estado: saldada ? "total" : "parcial",
    neto,
    aplicado,
    saldo,
    porConcepto: filas.map((f) => ({
      venta_tipo: f.venta_tipo,
      venta_ref_id: f.venta_ref_id,
      venta_ref_index: f.venta_ref_index,
      monto_aplicado: Number(f?.monto_aplicado || 0),
    })),
  };
};
